/**
 * Página de Usuários (overhaul v4) — só admin.
 *
 * Lista todos os usuários (inclusive inativos), permite criar novos
 * (nome, email, papel, cor de etiqueta) e editar papel / cor / ativo.
 * O backend bloqueia desativar o último admin e o próprio usuário logado.
 */
import { useState } from 'react';
import { TopNav } from '../components/TopNav';
import { AvatarUsuario } from '../components/AvatarUsuario';
import {
  ehAdmin,
  useAtualizarUsuario,
  useCriarUsuario,
  useExcluirUsuario,
  useMe,
  useResetarSenhaUsuario,
  useUsuarios,
} from '../hooks';
import { ROTULOS_COR, type CorEtiqueta, type CriarUsuarioIn, type Papel, type UsuarioOut } from '../types';
import { AVATAR_COR } from '../utils';

const PAPEIS: { valor: Papel; rotulo: string }[] = [
  { valor: 'admin', rotulo: 'Admin' },
  { valor: 'gerente', rotulo: 'Gerente' },
  { valor: 'colaborador', rotulo: 'Colaborador' },
  { valor: 'testador', rotulo: 'Testador (privacidade forçada)' },
  { valor: 'audiencista', rotulo: 'Audiencista (apenas audiências)' },
  { valor: 'correspondente', rotulo: 'Correspondente (apenas concluídos)' },
];

type CorVal = '' | NonNullable<CorEtiqueta>;

const CORES: { valor: CorVal; rotulo: string; hex: string }[] = [
  { valor: '',           rotulo: '— sem cor —',  hex: '#9ca3af' },
  { valor: 'azul_claro', rotulo: 'Azul',         hex: '#38bdf8' },
  { valor: 'azul_escuro',rotulo: 'Azul escuro',  hex: '#1d4ed8' },
  { valor: 'roxo',       rotulo: 'Roxo',          hex: '#8b5cf6' },
  { valor: 'laranja',    rotulo: 'Laranja',        hex: '#f97316' },
  { valor: 'rosa',       rotulo: 'Rosa',           hex: '#ec4899' },
  { valor: 'teal',       rotulo: 'Teal',           hex: '#14b8a6' },
  { valor: 'lavanda',    rotulo: 'Lavanda',        hex: '#7986cb' },
];

function corLabel(c: CorEtiqueta): string {
  return c ? ROTULOS_COR[c] : '—';
}

/**
 * Seletor de cor de etiqueta — dropdown (modelo anterior). As cores são uma paleta
 * CURADA (mapeiam p/ Kanban + Google Calendar), por isso lista fechada, não RGB livre.
 */
function SeletorCor({ valor, onChange, disabled, className = '' }: {
  valor: CorVal; onChange: (v: CorVal) => void; disabled?: boolean; className?: string;
}) {
  return (
    <select
      value={valor}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as CorVal)}
      title={valor ? ROTULOS_COR[valor] : '— sem cor —'}
      className={`px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900/40 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 ${className}`}
    >
      {CORES.map((c) => <option key={c.valor || 'sem'} value={c.valor}>{c.rotulo}</option>)}
    </select>
  );
}

export function Usuarios({ embed = false }: { embed?: boolean } = {}) {
  const { data: me } = useMe();
  const podeGerir = !!me?.pode_gerir_usuarios;
  const souSuper = !!me?.super_admin;
  const { data: usuarios, isLoading } = useUsuarios(true);
  const criar = useCriarUsuario();
  const atualizar = useAtualizarUsuario();
  const resetar = useResetarSenhaUsuario();
  const excluir = useExcluirUsuario();
  const [erro, setErro] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [emailEdits, setEmailEdits] = useState<Record<number, string>>({});
  const [avatarBust, setAvatarBust] = useState<Record<number, number>>({});

  function enviarAvatar(uid: number) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.webp';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const fd = new FormData();
      fd.append('arquivo', f);
      setErro(null); setMsg(null);
      try {
        const r = await fetch(`/api/usuarios/${uid}/avatar`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail ?? `HTTP ${r.status}`);
        setAvatarBust((m) => ({ ...m, [uid]: Date.now() }));
        setMsg('✅ avatar atualizado');
      } catch (e: any) {
        setErro(`avatar: ${e.message}`);
      }
    };
    input.click();
  }

  async function salvarEmail(id: number, atual: string) {
    const novo = (emailEdits[id] ?? '').trim().toLowerCase();
    if (!novo || novo === atual) return;
    setErro(null); setMsg(null);
    try {
      await atualizar.mutateAsync({ id, body: { email: novo } });
      setMsg('Email atualizado.');
      setEmailEdits((m) => { const c = { ...m }; delete c[id]; return c; });
    } catch (err: any) {
      if (err.status === 409) setErro('Já existe um usuário com esse email.');
      else setErro(err?.message ?? 'Erro ao atualizar email');
    }
  }

  // Senha provisória gerada na criação/reset — exibida 1× (não é recuperável depois).
  const [credencial, setCredencial] = useState<{ nome: string; senha: string } | null>(null);

  async function excluirUsuario(id: number, nome: string) {
    if (!confirm(
      `Excluir DEFINITIVAMENTE ${nome}?\n\n` +
      'Esta ação é IRREVERSÍVEL — o usuário some do sistema. ' +
      'Os cards que estavam com ele(a) ficam sem dono (podem ser reassumidos).\n\n' +
      'Dica: se a intenção é só bloquear o acesso, use “✓ Liberado → ✕ Restrito” em vez de excluir.'
    )) return;
    setErro(null); setMsg(null);
    try {
      await excluir.mutateAsync(id);
      setMsg(`Usuário ${nome} excluído.`);
    } catch (err: any) {
      if (err.status === 400) setErro(err.body || 'Não é possível excluir (ex: último admin ou você mesmo).');
      else if (err.status === 403) setErro(err.body || 'Sem permissão para excluir este usuário.');
      else setErro(err?.message ?? 'Erro ao excluir usuário');
    }
  }

  async function resetarSenha(id: number, nome: string) {
    if (!confirm(`Resetar a senha de ${nome}? Será gerada uma senha provisória nova e ele(a) terá que trocá-la no próximo acesso.`)) return;
    setErro(null); setMsg(null);
    try {
      const r = await resetar.mutateAsync(id);
      setCredencial({ nome, senha: r.senha_provisoria });
    } catch (err: any) {
      setErro(err?.message ?? 'Erro ao resetar senha');
    }
  }

  // Form de criação
  const [novoNome, setNovoNome] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [novoPapel, setNovoPapel] = useState<Papel>('colaborador');
  const [novaCor, setNovaCor] = useState<CorVal>('');

  async function handleCriar(e: React.FormEvent) {
    e.preventDefault();
    if (!novoNome.trim() || !novoEmail.trim()) return;
    setErro(null);
    const body: CriarUsuarioIn = {
      nome: novoNome.trim(),
      email: novoEmail.trim(),
      papel: novoPapel,
      cor_etiqueta: novaCor || null,
    };
    try {
      const novo = await criar.mutateAsync(body);
      setCredencial({ nome: body.nome, senha: novo.senha_provisoria });
      setNovoNome('');
      setNovoEmail('');
      setNovoPapel('colaborador');
      setNovaCor('');
    } catch (err: any) {
      if (err.status === 409) setErro('Já existe um usuário com esse email.');
      else if (err.status === 403) setErro('Sem permissão.');
      else setErro(err?.message ?? 'Erro ao criar usuário');
    }
  }

  async function patch(id: number, body: Parameters<typeof atualizar.mutateAsync>[0]['body']) {
    setErro(null);
    try {
      await atualizar.mutateAsync({ id, body });
    } catch (err: any) {
      if (err.status === 400) setErro(err.body || 'Operação não permitida (ex: último admin).');
      else if (err.status === 403) setErro('Sem permissão.');
      else setErro(err?.message ?? 'Erro ao atualizar usuário');
    }
  }

  if (!podeGerir) {
    const aviso = (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 rounded p-4 text-sm">
        🔒 Esta página é exclusiva para o gestor de usuários.
      </div>
    );
    if (embed) return aviso;
    return (
      <div>
        <TopNav />
        <main className="max-w-screen-md mx-auto p-6">{aviso}</main>
      </div>
    );
  }

  const inputClasse =
    'text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

  const conteudo = (
    <>
        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">👥 Usuários &amp; acesso</h1>

        <div className="text-xs text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 leading-relaxed">
          🔐 <strong>Controle de acesso:</strong> só usuários <strong>ativos</strong> conseguem entrar
          (por senha <em>e</em> por Google). Para <strong>liberar</strong>, cadastre/ative o usuário com o
          email Google dele; para <strong>restringir</strong>, basta desativá-lo. O email aqui é o que vale no
          login com Google. Use “Resetar senha” para gerar uma senha provisória nova (o usuário troca no próximo acesso).
        </div>

        {credencial && (
          <div className="text-sm bg-amber-50 dark:bg-amber-900/20 p-4 rounded border-2 border-amber-300 dark:border-amber-700">
            <div className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
              🔑 Senha provisória de {credencial.nome}
            </div>
            <div className="text-amber-700 dark:text-amber-300 mb-2 text-xs">
              Anote e entregue ao usuário agora — <strong>ela não aparece de novo</strong>. Ele(a) troca no 1º acesso.
            </div>
            <div className="flex items-center gap-2">
              <code className="px-3 py-1.5 bg-white dark:bg-gray-900 rounded border border-amber-300 dark:border-amber-700 font-mono text-base tracking-wide select-all">
                {credencial.senha}
              </code>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(credencial.senha); setMsg('Senha copiada.'); }}
                className="px-3 py-1.5 text-xs rounded bg-amber-600 hover:bg-amber-700 text-white"
              >
                Copiar
              </button>
              <button
                type="button"
                onClick={() => setCredencial(null)}
                className="px-3 py-1.5 text-xs rounded border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300"
              >
                Já anotei
              </button>
            </div>
          </div>
        )}

        {msg && (
          <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded border border-emerald-200 dark:border-emerald-800">
            ✓ {msg}
          </div>
        )}

        {erro && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-200 dark:border-red-800">
            ⚠ {erro}
          </div>
        )}

        {/* Form de criação */}
        <form
          onSubmit={handleCriar}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
        >
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            Cadastrar novo usuário
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="lg:col-span-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Nome</label>
              <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} className={`${inputClasse} w-full`} placeholder="Nome" />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Email</label>
              <input value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} type="email" className={`${inputClasse} w-full`} placeholder="email@exemplo.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Papel</label>
              <select value={novoPapel} onChange={(e) => setNovoPapel(e.target.value as Papel)} className={`${inputClasse} w-full`}>
                {PAPEIS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
              </select>
            </div>
              <div>
               <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cor etiqueta</label>
               <SeletorCor valor={novaCor} onChange={(v) => setNovaCor(v as any)} className="w-full" />
             </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={criar.isPending || !novoNome.trim() || !novoEmail.trim()}
              className="px-2.5 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-medium"
            >
              {criar.isPending ? '⏳ Criando…' : '➕ Cadastrar'}
            </button>
          </div>
        </form>

        {/* Tabela de usuários */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-gray-400">carregando…</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-2.5 py-2">Nome</th>
                  <th className="px-2.5 py-2">Email (Google)</th>
                  <th className="px-2.5 py-2">Papel</th>
                  {souSuper && <th className="px-2.5 py-2" title="Super usuário — acesso irrestrito + Gestão de Escritórios">SU</th>}
                  <th className="px-2.5 py-2" title="A busca automática de prazos (DJEN) aponta para estas OABs">OAB</th>
                  <th className="px-2.5 py-2">Cor GCal</th>
                  <th className="px-2.5 py-2">Acesso</th>
                  <th className="px-2.5 py-2">Senha</th>
                  <th className="px-2.5 py-2">Excluir</th>
                </tr>
              </thead>
              <tbody>
                {(usuarios ?? []).map((u: UsuarioOut) => {
                  const ehEu = u.id === me?.id;
                  return (
                    <tr key={u.id} className={`border-b border-gray-100 dark:border-gray-700/50 ${!u.ativo ? 'opacity-40' : ''}`}>
                      <td className="px-2.5 py-2 font-medium text-gray-800 dark:text-gray-100">
                        <span className="inline-flex items-center gap-2.5">
                          <button
                            type="button"
                            onClick={() => enviarAvatar(u.id)}
                            title="Clique para trocar a foto"
                            className="rounded-full hover:ring-2 hover:ring-blue-400 transition"
                          >
                            <AvatarUsuario userId={u.id} nome={u.nome}
                              corEtiqueta={u.cor_etiqueta} bust={avatarBust[u.id]} />
                          </button>
                          <span>
                            <span className="block font-semibold">{u.nome}</span>
                            {ehEu && <span className="text-[10px] text-blue-500 font-normal">(você)</span>}
                          </span>
                        </span>
                      </td>
                      <td className="px-2.5 py-2">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="email"
                            value={emailEdits[u.id] ?? u.email}
                            onChange={(e) => setEmailEdits((m) => ({ ...m, [u.id]: e.target.value }))}
                            className={`${inputClasse} py-1 w-44`}
                            placeholder="email@gmail.com"
                          />
                          {(emailEdits[u.id] ?? u.email).trim().toLowerCase() !== u.email && (
                            <button
                              onClick={() => salvarEmail(u.id, u.email)}
                              disabled={atualizar.isPending}
                              title="Salvar email"
                              className="px-1.5 py-1 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              💾
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2.5 py-2">
                        <select
                          value={u.papel}
                          onChange={(e) => patch(u.id, { papel: e.target.value as Papel })}
                          disabled={atualizar.isPending}
                          className={`${inputClasse} py-1`}
                        >
                          {PAPEIS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
                        </select>
                      </td>
                      {souSuper && (
                        <td className="px-2.5 py-2">
                          <button
                            onClick={() => patch(u.id, { super_admin: !u.super_admin } as any)}
                            disabled={atualizar.isPending}
                            title={u.super_admin ? 'Rebaixar de super usuário' : 'Promover a super usuário (acesso irrestrito + Gestão de Escritórios)'}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              u.super_admin
                                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                                : 'bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400'
                            } disabled:opacity-40`}
                          >
                            {u.super_admin ? '★ SU' : '☆ —'}
                          </button>
                        </td>
                      )}
                      <td className="px-2.5 py-2">
                        <input
                          defaultValue={u.oab_numero ? `${u.oab_numero}/${u.oab_uf ?? ''}` : ''}
                          placeholder="123456/RJ"
                          title="OAB do advogado — a busca de prazos do DJEN aponta para ela"
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            const atual = u.oab_numero ? `${u.oab_numero}/${u.oab_uf ?? ''}` : '';
                            if (v === atual) return;
                            const m = v.match(/^(\d{4,6})\s*\/?\s*([A-Za-z]{2})?$/);
                            if (!v) { patch(u.id, { oab_numero: '', oab_uf: '' } as any); return; }
                            if (!m) { setErro('OAB inválida — use o formato 123456/RJ'); return; }
                            patch(u.id, { oab_numero: m[1], oab_uf: (m[2] ?? '').toUpperCase() } as any);
                          }}
                          className={`${inputClasse} py-1 w-24`}
                        />
                      </td>
                      <td className="px-2.5 py-2">
                        <SeletorCor
                          valor={(u.cor_etiqueta ?? '') as CorVal}
                          disabled={atualizar.isPending}
                          onChange={(v) => patch(u.id, { cor_etiqueta: (v || null) as CorEtiqueta })}
                        />
                      </td>
                      <td className="px-2.5 py-2">
                        <button
                          onClick={() => patch(u.id, { ativo: !u.ativo })}
                          disabled={atualizar.isPending || ehEu}
                          title={ehEu ? 'Você não pode restringir a si mesmo' : u.ativo ? 'Restringir acesso' : 'Liberar acesso'}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            u.ativo
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300'
                          } disabled:opacity-40`}
                        >
                          {u.ativo ? '✓ Liberado' : '✕ Restrito'}
                        </button>
                      </td>
                      <td className="px-2.5 py-2">
                        <button
                          onClick={() => resetarSenha(u.id, u.nome)}
                          disabled={resetar.isPending}
                          title="Gerar uma senha provisória nova e forçar troca no próximo acesso"
                          className="px-2 py-1 rounded text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-40"
                        >
                          🔑 Resetar
                        </button>
                      </td>
                      <td className="px-2.5 py-2">
                        <button
                          onClick={() => excluirUsuario(u.id, u.nome)}
                          disabled={excluir.isPending || ehEu}
                          title={ehEu ? 'Você não pode excluir a si mesmo' : 'Excluir definitivamente este usuário'}
                          className="px-2 py-1 rounded text-xs font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-40"
                        >
                          🗑️ Excluir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
    </>
  );

  if (embed) return <div className="space-y-6">{conteudo}</div>;
  return (
    <div>
      <TopNav />
      <main className="max-w-screen-xl mx-auto p-4 space-y-6">{conteudo}</main>
    </div>
  );
}
